import { isNumber, isPartialDate, partialDateToValue } from 'neuro-utils';

import type { IAddon, IData, IDataPoint, IEvent, IItem, TDescriptionTable } from 'Components/sq-graphics';
import { steps as mgcSteps } from 'Routes/FunctionalPerformanceMgravis/Document/Form/mgc';
import { sortDocuments } from 'Utility/randomUtil';
import { passQuestion, mgadl, Doc_Progress, mgc } from 'neuro-schemas';
import { mgadlSteps } from 'Routes/MyService/Document/Form/SurveyForms/MGADL';

const { calculateProgress: calculateMGCProgress, calculateScore: mgcScore } = mgc;
const isMGCComplete = (doc: IMGC): boolean => calculateMGCProgress(doc).state === Doc_Progress.State.Completed;

const changeToLastMGC = (thisDoc: IMGC, lastDoc: IMGC) => {
  const changes: Array<{ step: string; thisData: number | string; lastData: number | string }> = [];
  if (!isMGCComplete(thisDoc) || !isMGCComplete(lastDoc)) return changes;
  mgcSteps.forEach(step => {
    const thisData = thisDoc[step] as number;
    const lastData = lastDoc[step] as number;
    if (thisData !== lastData) changes.push({ step, thisData, lastData });
  });
  return changes;
};

export const convertMGCToGraph = (
  docs: IDocumentWithTypeV2<'mgc'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];

  const sortedAndFilteredDocs = docs
    .filter(d => {
      const isComplete = isMGCComplete(d);
      return (isComplete || d.manualScore || d.manualScore === 0) && isPartialDate(d.date);
    })
    .sort((d1, d2) => sortDocuments([{ type: 'date', sortField: 'date' }])(d1, d2));

  sortedAndFilteredDocs.forEach((d, i) => {
    const nextDoc = sortedAndFilteredDocs[i + 1];
    const score = isNumber(d.manualScore) ? d.manualScore : mgcScore(d);
    if (isPartialDate(d.date) && isNumber(score)) {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: score,
        id: 'mgc',
        description:
          nextDoc && changeToLastMGC(d, nextDoc).length > 0 ? (
            <div>
              {changeToLastMGC(d, nextDoc).map(change => (
                <div key={change.step}>
                  {`${fm('functionalPerformanceMgravis.labels.mgc.' + change.step)}: `}{' '}
                  <span style={{ fontWeight: 600 }}>{change.lastData}</span>
                  {' -> '}
                  <span style={{ fontWeight: 600 }}>{change.thisData}</span>
                </div>
              ))}
            </div>
          ) : !isMGCComplete(d) && (d.manualScore || d.manualScore === 0) ? (
            fm('functionalPerformanceMgravis.manuallyEnteredScore')
          ) : undefined,
      });
    }
  });
  return [
    {
      dataPoints,
      id: 'mgc',
      legend: fm('functionalPerformanceMgravis.mgcShort'),
      type: 'lineGraph',
    },
  ];
};

const isFilledMgadl = (doc: IMGADL): boolean => {
  return mgadl.calculateProgress(doc).state === Doc_Progress.State.Completed;
};

const changeToLastMGADL = (thisDoc: IMGADL, lastDoc: IMGADL) => {
  const changes: Array<{ step: string; thisData: number; lastData: number }> = [];
  if (
    !isFilledMgadl(thisDoc) ||
    thisDoc.manualScore ||
    thisDoc.manualScore === 0 ||
    !isFilledMgadl(lastDoc) ||
    lastDoc.manualScore ||
    lastDoc.manualScore === 0
  )
    return changes;
  mgadlSteps.forEach(step => {
    const thisData = thisDoc[step] as number;
    const lastData = lastDoc[step] as number;
    if (thisData !== lastData) changes.push({ step, thisData, lastData });
  });
  return changes;
};

export const convertMGADLToGraph = (
  docs: Array<IDocumentWithTypeV2<'mgadl'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  uiLang: Locale,
  altDocs?: Array<IDocumentWithTypeV2<'mgadl'>>,
): Array<IData> | undefined => {
  if (docs.length === 0 && altDocs?.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  const altDataPoints: IDataPoint[] = [];

  const sortedAndFilteredDocs = docs
    .filter(d => isFilledMgadl(d))
    .sort((d1, d2) => sortDocuments([{ type: 'date', sortField: 'date' }])(d1, d2));

  const sortedAltDocs = altDocs
    ?.filter(d => isFilledMgadl(d))
    .sort((d1, d2) => sortDocuments([{ type: 'date', sortField: 'date' }])(d1, d2));

  const formatData = (docs: Array<IDocumentWithTypeV2<'mgadl'>>, dataPointsArray: IDataPoint[]) => {
    return docs.forEach((d, i) => {
      const nextDoc = docs[i + 1];
      d.date &&
        dataPointsArray.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          value: d.manualScore || d.manualScore === 0 ? d.manualScore : (mgadl.calculateScore(d) ?? '-'),
          id: 'mgadl',
          description:
            nextDoc && changeToLastMGADL(d, nextDoc).length > 0 ? (
              <div>
                {changeToLastMGADL(d, nextDoc).map(change => (
                  <div key={change.step}>
                    {`${mgadl.localizations[uiLang][`labels.${change.step}`]}: `}{' '}
                    <span style={{ fontWeight: 600 }}>{change.lastData}</span>
                    {' -> '}
                    <span style={{ fontWeight: 600 }}>{change.thisData}</span>
                  </div>
                ))}
              </div>
            ) : d.manualScore || d.manualScore === 0 ? (
              fm('functionalPerformanceMgravis.manuallyEnteredScore')
            ) : undefined,
        });
    });
  };

  formatData(sortedAndFilteredDocs, dataPoints);
  sortedAltDocs && formatData(sortedAltDocs, altDataPoints);

  return [
    {
      dataPoints,
      id: 'mgadl',
      legend: mgadl.localizations[uiLang]['mgadlShort'],
      type: 'lineGraph',
    },
    {
      dataPoints: altDataPoints,
      id: 'mgadlMy',
      legend: mgadl.localizations[uiLang]['mgadlShort'] + ' ' + fm('functionalPerformanceMgravis.patientReported'),
      type: 'lineGraph',
    },
  ];
};

export const convertMGFAToTimeline = (
  docs: Array<IDocumentWithTypeV2<'mgfa'>>,
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  const events: Array<IEvent> = [];

  const formatClassification = (classification?: string): string => {
    if (typeof classification !== 'string') return '-';

    return `${
      classification.includes('a') || classification.includes('b')
        ? `${fm(`diagnosis.mgravis.mgfa.opts.${classification.substring(0, classification.length - 1)}`)} `
        : ''
    }${fm(`diagnosis.mgravis.mgfa.opts.${classification}`)}`;
  };

  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    doc.classification &&
      events.push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        eventType: 'text',
        title: `${doc.classification}`,
        description: { value: formatClassification(doc.classification) },
      });
  });
  addons.push({
    id: 'topdata',
    title: fm('diagnosis.mgravis.mgfa.mgfaShort'),
    titleDescription: undefined,
    items: [],
    events,
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertSymptomsToTimeline = (
  docs: IDocumentWithTypeV2<'mgravisSymptoms'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (name: string, values?: Record<string, any>) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;

    const events: IEvent[] = [];
    events.push({
      date: dateFromPartialUpdateTimeframe(doc.date, undefined, true),
      eventType: 'themedDiamondWithText',
      title: fm('diagnosis.mgravis.symptoms.title'),
      description: [
        {
          title: fm('diagnosis.mgravis.symptoms.location'),
          values: Array.isArray(doc.symptomLocation) && doc.symptomLocation.length > 0 && (
            <div>
              {doc.symptomLocation.map(loc => (
                <div style={{ width: '100%', display: 'block' }} key={loc}>
                  {fm('diagnosis.mgravis.symptoms.' + loc)}
                </div>
              ))}
            </div>
          ),
        },
      ],
      priority: 'high',
      iconText: fm('diagnosis.mgravis.symptoms.year', { year: doc.age }),
    });
    addons.push({
      id: 'earlySymptoms',
      title: fm('diagnosis.mgravis.symptoms.title').toLocaleUpperCase(),
      titleDescription: undefined,
      items: [],
      events,
    });
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertPassQuestionToTimeline = (
  docs: Array<IPassQuestion & IControlProps>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  locale: Locale,
  fm: (name: string, values?: Record<string, any>) => string,
): Array<IAddon> | undefined => {
  const locales = passQuestion.localizations[locale];

  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  const events: Array<IEvent> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    doc.satisfiedWithCurrentSituation &&
      events.push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        eventType: 'activity',
        priority: doc.satisfiedWithCurrentSituation === 'no' ? 'high' : 'low',
        title: fm('myService.mgravis.opts.passQuestion.' + doc.satisfiedWithCurrentSituation),
      });
  });
  addons.push({
    id: 'passQuestion',
    title: locales['title'],
    titleDescription: undefined,
    items: [],
    events,
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertHospitalizationToTimelineMgravis = (
  docs: Array<IDocumentWithTypeV2<'hospitalization'>>,
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  const singleDayEventsHospitalWard: IEvent[] = [];
  const singleDayEventsIntensive: IEvent[] = [];
  const itemsHospitalWard: IItem[] = [];
  const itemsIntesive: IItem[] = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    if (!doc.department) return;
    (doc.department === 'hospitalWard' ? itemsHospitalWard : itemsIntesive).push({
      start: dateFromPartialUpdateTimeFrame(doc.date),
      end: isPartialDate(doc.endDate) ? dateFromPartialUpdateTimeFrame(doc.endDate) : undefined,
      title: fm(`comorbidity.mgravis.filter.${doc.department}`),
      themeId:
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        doc.department === 'intensiveCare' ? 'flirt' : doc.department === 'hospitalWard' ? 'warningLight' : undefined,
    });
    if (partialDateToValue(doc.date) === partialDateToValue(doc.endDate)) {
      (doc.department === 'hospitalWard' ? singleDayEventsHospitalWard : singleDayEventsIntensive).push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        title: fm(`comorbidity.mgravis.filter.${doc.department}`),
        eventType: 'treatment',
      });
    }
  });

  if (itemsHospitalWard.length > 0 || singleDayEventsHospitalWard.length > 0) {
    addons.push({
      id: 'hospitalWard',
      title: fm('comorbidity.mgravis.filter.hospitalWard'),
      items: itemsHospitalWard,
      events: singleDayEventsHospitalWard,
      themeId: 'hospitalWardHospitalization',
    });
  }
  if (itemsIntesive.length > 0 || singleDayEventsIntensive.length > 0) {
    addons.push({
      id: 'intensiveCare',
      title: fm('comorbidity.mgravis.filter.intensiveCare'),
      items: itemsIntesive,
      events: singleDayEventsIntensive,
      themeId: 'intensiveCareHospitalization',
    });
  }

  return addons.length > 0 ? addons : undefined;
};

export const convertProcedureToTimeline = (
  docs: IDocumentWithTypeV2<'procedure'>[],
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return;
  const thymectomyEvents: IEvent[] = [];
  const plasmaChangeEvents: IEvent[] = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    if (!doc.code) return;
    if (doc.code.code === '19647005') {
      plasmaChangeEvents.push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        title: fm('procedure.plasmaChange'),
        eventType: 'treatment',
        description: doc.additionalInformation,
      });
    } else if (doc.code.code === '399750009') {
      thymectomyEvents.push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        title: fm('procedure.thymectomy'),
        eventType: 'treatment',
        description: [
          {
            title: fm('procedure.finding.pad'),
            values: Array.isArray(doc.finding)
              ? doc.finding.map(find => fm(`procedure.finding.${find}`)).join(', ')
              : doc.finding
                ? fm(`procedure.finding.${doc.finding}`)
                : '-',
            condition: !!doc.finding,
          },
          {
            title: fm('procedure.additionalInformation'),
            values: doc.additionalInformation,
            condition: !!doc.additionalInformation,
          },
        ],
      });
    }
  });
  if (thymectomyEvents.length === 0 && plasmaChangeEvents.length === 0) return;

  return [
    ...(plasmaChangeEvents.length > 0
      ? [
          {
            id: '19647005',
            title: fm('procedure.plasmaChange'),
            events: plasmaChangeEvents,
          },
        ]
      : []),
    ...(thymectomyEvents.length > 0
      ? [
          {
            id: '399750009',
            title: fm('procedure.thymectomy'),
            events: thymectomyEvents,
          },
        ]
      : []),
  ];
};

const getCnpExamDesc = (doc: IDiagnosticExamination, fm: (id: string) => string): TDescriptionTable => {
  return [
    {
      title: fm('clpAndCnpTests.mgravis.findingType'),
      values: fm(`clpAndCnpTests.mgravis.opts.${doc.finding?.type}`),
      condition: !!doc.finding?.type,
    },
    {
      title: fm('clpAndCnpTests.mgravis.abnormalDetails'),
      values: doc.finding?.abnormalDetails,
      condition: !!doc.finding?.abnormalDetails,
    },
    {
      title: fm('clpAndCnpTests.mgravis.supportDiagnosis'),
      values: fm(`general.${!!doc.finding?.supportDiagnosis}`),
      condition: doc.finding?.type === 'abnormal',
    },
  ];
};

export const convertCnpExamsToTimeline = (
  docs: Array<IDocumentWithTypeV2<'diagnosticExamination'>>,
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return;
  const codes = ['XA402', '252748005', '252756008'];
  const enmgEvents: IEvent[] = [];
  const repeatStimulationEvents: IEvent[] = [];
  const singleFiberEMGEvents: IEvent[] = [];
  const events: Record<string, IEvent[]> = {
    XA402: enmgEvents,
    '252748005': repeatStimulationEvents,
    '252756008': singleFiberEMGEvents,
  };
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (!d.code) return;
    if (!codes.includes(d.code.code)) return;
    events[d.code.code]?.push({
      date: dateFromPartialUpdateTimeFrame(d.date),
      title: fm(`clpAndCnpTests.mgravis.${d.code.code}`),
      description: getCnpExamDesc(d, fm),
      eventType: 'treatment',
    });
  });
  if (enmgEvents.length === 0 && repeatStimulationEvents.length === 0 && singleFiberEMGEvents.length === 0) return;
  return codes
    .map(c => ({
      id: c,
      title: fm(`clpAndCnpTests.mgravis.${c}`),
      events: events[c],
    }))
    .filter(addon => (addon.events ?? []).length !== 0);
};

export const convertDiagnosticTestsToTimeline = (
  docs: Array<IDocumentWithTypeV2<'diagnosticExamination'>>,
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return;
  const codes = ['1004087002', '1004088007'];
  const neostigmineTestEvents: IEvent[] = [];
  const iceCubeTestEvents: IEvent[] = [];
  const events: Record<string, IEvent[]> = {
    '1004087002': neostigmineTestEvents,
    '1004088007': iceCubeTestEvents,
  };
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (!d.code) return;
    if (!codes.includes(d.code.code)) return;
    events[d.code.code]?.push({
      date: dateFromPartialUpdateTimeFrame(d.date),
      title: fm(`diagnosticTests.${d.code.code}`),
      description: [
        {
          title: fm('diagnosticTests.interpretation'),
          values: fm(`diagnosticTests.opts.${d.finding?.supportDiagnosis ? 'support' : 'noSupport'}`),
          condition: typeof d.finding?.supportDiagnosis === 'boolean',
        },
      ],
      eventType: 'treatment',
    });
  });
  if (neostigmineTestEvents.length === 0 && iceCubeTestEvents.length === 0) return;
  return codes
    .map(c => ({
      id: c,
      title: fm(`diagnosticTests.${c}`),
      events: events[c],
    }))
    .filter(addon => (addon.events ?? []).length !== 0);
};
